import { Component, Input, OnInit } from '@angular/core';

@Component({
  selector: 'app-approvation-steps',
  templateUrl: './approvation-steps.component.html',
  styleUrls: ['./approvation-steps.component.scss'],
})
export class ApprovationStepsComponent implements OnInit {
  @Input() approval: UserApprovalResponse;

  constructor() {}

  ngOnInit(): void {}

  /**
   * Verifica se o cadastro está rejeitado
   */
  isRejected(): boolean {
    if (!this.approval?.register?.current_stage) {
      return false;
    }
    return this.approval.register.current_stage === 'Rejeitado';
  }

  /**
   * Verifica se um departamento específico foi rejeitado
   */
  isDepartmentRejected(item: UserApprovalQueueDepartment): boolean {
    if (!this.isRejected() || !this.approval?.signatures) {
      return false;
    }

    // Verifica se há uma assinatura rejeitada para este departamento
    const rejectedSign = this.approval.signatures.find(
      (signature) =>
        signature?.department_approval?.id === item.id &&
        signature?.approved === false &&
        signature?.attempt === this.approval.attempt
    );

    return !!rejectedSign;
  }

  isCurrentDepartment(item: UserApprovalQueueDepartment, index: number) {
    if (!this.approval || !item) {
      return false;
    }

    // Se estiver rejeitado, só marca como completo o departamento que foi rejeitado
    if (this.isRejected()) {
      return this.isDepartmentRejected(item);
    }

    if (this.isRepresentativeBlocking()) {
      return false;
    }

    if (this.approval.completed) {
      return true;
    }

    // Se o departamento foi completado (considerando queue status), está no departamento atual ou já passou
    if (this.getDepartmentCompleted(item)) {
      return true;
    }

    const is = (this.approval.step || 0) >= (item.order || 0);

    return is;
  }

  getRepresentativeSignStatus() {
    const status = (this.approval as any)?.representative_sign_status;
    if (!status || typeof status.total !== 'number') {
      return null;
    }
    return {
      total: status.total,
      signed: status.signed || 0,
    };
  }

  hasRepresentativeSignStep(): boolean {
    const status = this.getRepresentativeSignStatus();
    return !!status && status.total > 0;
  }

  isRepresentativeSignComplete(): boolean {
    const status = this.getRepresentativeSignStatus();
    if (!status) {
      return false;
    }
    return status.signed >= status.total && status.total > 0;
  }

  shouldCompleteLineFromEdit(): boolean {
    if (this.isRejected()) {
      return true;
    }
    if (this.approval?.completed || this.isHomologated()) {
      return true;
    }
    if (this.hasRepresentativeSignStep()) {
      return true;
    }
    return (this.approval?.step || 0) > 0;
  }

  getDepartmentCompleted(item: UserApprovalQueueDepartment) {
    if (!this.approval || !item) {
      return false;
    }

    // Se estiver rejeitado, só marca como completo o departamento que foi rejeitado
    if (this.isRejected()) {
      return this.isDepartmentRejected(item);
    }

    if (this.isRepresentativeBlocking()) {
      return false;
    }

    if (this.approval.completed) {
      return true;
    }

    // Verificar status da queue se disponível (campos adicionais da API v3)
    const queueDept = this.approval.queue?.departments?.find(
      (dept) => dept.id === item.id
    );
    if (queueDept) {
      // Verificar se tem status "concluído" ou "assinado"
      const status = (queueDept as any).status;
      if (status === 'concluído' || status === 'assinado') {
        return true;
      }
      // Verificar se signed é true e signed_count >= sign_quantity
      const signed = (queueDept as any).signed;
      const signedCount = (queueDept as any).signed_count || 0;
      if (signed && signedCount >= (item.sign_quantity || 1)) {
        return true;
      }
    }

    // Verificar signatures (lógica original)
    if (!this.approval.signatures || !Array.isArray(this.approval.signatures)) {
      return false;
    }

    const attemptSigns = this.approval.signatures
      .filter(
        (signature) =>
          signature?.attempt === this.approval.attempt &&
          signature?.department_approval?.id === item.id
      )
      .map((signature) => signature.department_approval);

    if (attemptSigns.length >= (item.sign_quantity || 1)) {
      return true;
    }

    return false;
  }

  getDepartmentSigns(department: UserApprovalQueueDepartment) {
    if (!this.approval || !department || !this.approval.signatures || !Array.isArray(this.approval.signatures)) {
      return [];
    }

    return this.approval.signatures.filter(
      (signature) =>
        signature?.department_approval?.id === department.id &&
        signature?.attempt === this.approval.attempt
    );
  }

  getAgentName(agent) {
    if (!agent) {
      return '';
    }
    if (Array.isArray(agent)) {
      return agent.map((_) => _?.name || '').filter(name => name).join(', ');
    } else {
      return agent?.name || '';
    }
  }

  getRegisterName(register) {
    if (!register) {
      return '';
    }
    // Verificar primeiro se tem person ou company
    if (register.person) {
      return register.person?.full_name || '';
    }
    if (register.company) {
      return register.company?.corporate_name || '';
    }
    // Fallback para register.name (usado em signatures)
    return register.name || '';
  }

  formatDateTime(date: string) {
    if (!date) {
      return '';
    }
    try {
      const newDate = new Date(date);
      if (isNaN(newDate.getTime())) {
        return '';
      }
      // A data já vem em UTC da API, o toLocaleString() converte automaticamente para o timezone local
      // Não é necessário adicionar 3 horas manualmente
      return newDate.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch (error) {
      return '';
    }
  }

  /**
   * Verifica se o cadastro está homologado
   */
  isHomologated(): boolean {
    return this.approval?.completed && (this.approval?.is_approved || this.approval?.homologated);
  }

  /**
   * Verifica se todos os departamentos foram aprovados
   */
  allDepartmentsCompleted(): boolean {
    if (!this.approval?.queue?.departments || this.approval.queue.departments.length === 0) {
      return false;
    }

    return this.approval.queue.departments.every(dept => this.getDepartmentCompleted(dept));
  }

  /**
   * Verifica se a linha que chega ATÉ este departamento deve estar completa
   * Quando rejeitado, a linha deve chegar até o departamento rejeitado
   */
  shouldCompleteLineToDepartment(item: UserApprovalQueueDepartment, index: number): boolean {
    if (!this.approval || !item) {
      return false;
    }

    // Se estiver homologado, todas as linhas estão completas
    if (this.isHomologated()) {
      return true;
    }

    if (this.isRepresentativeBlocking()) {
      return false;
    }

    // Se estiver rejeitado, a linha deve chegar até o departamento rejeitado
    if (this.isRejected()) {
      // Encontra o índice do departamento rejeitado
      const rejectedIndex = this.approval.queue?.departments?.findIndex(
        dept => this.isDepartmentRejected(dept)
      );
      
      // Se encontrou um departamento rejeitado, a linha deve chegar até ele (inclusive)
      if (rejectedIndex !== undefined && rejectedIndex !== -1) {
        return index <= rejectedIndex;
      }
    }

    // Lógica normal: linha completa se chegou até este departamento
    return this.isCurrentDepartment(item, index);
  }

  /**
   * Verifica se a linha que sai DESTE departamento deve estar completa
   * Quando rejeitado, a linha NÃO deve sair do departamento rejeitado
   */
  shouldCompleteLineFromDepartment(item: UserApprovalQueueDepartment): boolean {
    if (!this.approval || !item) {
      return false;
    }

    // Se estiver homologado, todas as linhas estão completas
    if (this.isHomologated()) {
      return true;
    }

    if (this.isRepresentativeBlocking()) {
      return false;
    }

    // Se estiver rejeitado, a linha NÃO deve sair do departamento rejeitado
    if (this.isRejected()) {
      // Se este departamento foi rejeitado, a linha não deve sair dele
      if (this.isDepartmentRejected(item)) {
        return false;
      }
    }

    // Lógica normal: linha completa se o departamento foi completado
    return this.getDepartmentCompleted(item);
  }

  private isRepresentativeBlocking(): boolean {
    return this.hasRepresentativeSignStep() && !this.isRepresentativeSignComplete();
  }

  /**
   * Retorna informações de quem aprovou
   */
  getApprovedBy(): ApprovalAction | null {
    return this.approval?.approved_by || null;
  }

  /**
   * Retorna informações de quem rejeitou
   */
  getRejectedBy(): ApprovalAction | null {
    return this.approval?.rejected_by || null;
  }

  /**
   * Retorna informações de quem homologou
   */
  getHomologatedBy(): ApprovalAction | null {
    return this.approval?.homologated_by || null;
  }

  /**
   * Retorna o reason da assinatura de homologação (justificativa), se existir.
   * Usa o reason da última assinatura quando homologado.
   */
  getHomologationReason(): string {
    if (!this.isHomologated() || !this.approval?.signatures?.length) {
      return '';
    }
    const sigs = this.approval.signatures as any[];
    const last = sigs[sigs.length - 1];
    const reason = last?.reason;
    return typeof reason === 'string' && reason.trim() ? reason.trim() : '';
  }

  /**
   * Retorna informações de quem retornou
   */
  getReturnedBy(): ApprovalAction | null {
    return this.approval?.returned_by || null;
  }

  /**
   * Formata o nome do usuário de uma ação de aprovação
   */
  getActionUserName(action: ApprovalAction | null): string {
    if (!action?.user) {
      return '';
    }
    return `${action.user.first_name || ''} ${action.user.last_name || ''}`.trim() || action.user.email || '';
  }

  /**
   * Retorna o nome do departamento de uma ação de aprovação
   */
  getActionDepartmentName(action: ApprovalAction | null): string {
    return action?.department?.name || '';
  }

  /**
   * Retorna o nome do agente de uma ação de aprovação (se existir)
   */
  getActionAgentName(action: ApprovalAction | null): string {
    return action?.agent?.name || '';
  }

  /**
   * Formata a data de uma ação de aprovação
   */
  formatActionDate(action: ApprovalAction | null): string {
    if (!action?.date) {
      return '';
    }
    return this.formatDateTime(action.date);
  }

  /**
   * Verifica se um departamento específico foi aprovado (baseado em approved_by)
   */
  isDepartmentApprovedBy(department: UserApprovalQueueDepartment): boolean {
    const approvedBy = this.getApprovedBy();
    if (!approvedBy || !approvedBy.department) {
      return false;
    }
    
    // Comparar pelo ID do departamento
    if (department.department && approvedBy.department.id === department.department.id) {
      return true;
    }
    
    // Comparar pelo nome do departamento (case-insensitive)
    const approvedDeptName = approvedBy.department.name?.toLowerCase()?.trim() || '';
    const deptName = department.department?.name?.toLowerCase()?.trim() || '';
    if (approvedDeptName && deptName && approvedDeptName === deptName) {
      return true;
    }
    
    // Se o department for um agente, comparar pelo nome do agente
    if (department.type === 'agent' && department.agent && approvedBy.agent) {
      const approvedAgentName = approvedBy.agent.name?.toLowerCase()?.trim() || '';
      const agentName = department.agent.name?.toLowerCase()?.trim() || '';
      if (approvedAgentName && agentName && approvedAgentName === agentName) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Verifica se um departamento específico foi rejeitado (baseado em rejected_by)
   */
  isDepartmentRejectedBy(department: UserApprovalQueueDepartment): boolean {
    const rejectedBy = this.getRejectedBy();
    if (!rejectedBy || !rejectedBy.department) {
      return false;
    }
    
    // Comparar pelo ID do departamento
    if (department.department && rejectedBy.department.id === department.department.id) {
      return true;
    }
    
    // Comparar pelo nome do departamento (case-insensitive)
    const rejectedDeptName = rejectedBy.department.name?.toLowerCase()?.trim() || '';
    const deptName = department.department?.name?.toLowerCase()?.trim() || '';
    if (rejectedDeptName && deptName && rejectedDeptName === deptName) {
      return true;
    }
    
    // Se o department for um agente, comparar pelo nome do agente
    if (department.type === 'agent' && department.agent && rejectedBy.agent) {
      const rejectedAgentName = rejectedBy.agent.name?.toLowerCase()?.trim() || '';
      const agentName = department.agent.name?.toLowerCase()?.trim() || '';
      if (rejectedAgentName && agentName && rejectedAgentName === agentName) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Verifica se um departamento específico foi retornado (baseado em returned_by)
   */
  isDepartmentReturnedBy(department: UserApprovalQueueDepartment): boolean {
    const returnedBy = this.getReturnedBy();
    if (!returnedBy || !returnedBy.department) {
      return false;
    }
    
    // Comparar pelo ID do departamento
    if (department.department && returnedBy.department.id === department.department.id) {
      return true;
    }
    
    // Comparar pelo nome do departamento (case-insensitive)
    const returnedDeptName = returnedBy.department.name?.toLowerCase()?.trim() || '';
    const deptName = department.department?.name?.toLowerCase()?.trim() || '';
    if (returnedDeptName && deptName && returnedDeptName === deptName) {
      return true;
    }
    
    // Se o department for um agente, comparar pelo nome do agente
    if (department.type === 'agent' && department.agent && returnedBy.agent) {
      const returnedAgentName = returnedBy.agent.name?.toLowerCase()?.trim() || '';
      const agentName = department.agent.name?.toLowerCase()?.trim() || '';
      if (returnedAgentName && agentName && returnedAgentName === agentName) {
        return true;
      }
    }
    
    return false;
  }
}
